import { HubFeature } from '@ats/api/cm';
import { LocDef, LocDefs, LocMap } from '@cm/core/localization';

const dpRoot = `Catalogs|Emails|Details|`;

const sharedProperties: Pick<LocDef, 'shared' | 'htmlAllowed' | 'feature' | 'portal'> = {
	shared: false,
	htmlAllowed: false,
	feature: HubFeature.Core,
	portal: 'SP',
};

type NameStart = 'SPEmailContent';
export type EmailContentLocs =
	| `${NameStart}PlainTextFieldLabel`
	| `${NameStart}HtmlBodyFieldLabel`
	| `${NameStart}NotificationTestSendComplete`
	| `${NameStart}TitleSection`
	| `${NameStart}ButtonSendTest`
	| `${NameStart}ButtonEdit`
	| `${NameStart}ButtonCancel`
	| `${NameStart}ButtonSave`
	| `${NameStart}LabelLanguage`
	| `${NameStart}ButtonAddLanguage`
	| `${NameStart}LabelLastUpdatedBy`
	| `${NameStart}LabelLastUpdated`
	| `${NameStart}LabelSubject`
	| `${NameStart}TitleSubstitutionTokens`
	| `${NameStart}ButtonHideTokens`
	| `${NameStart}ButtonShowTokens`;

export const emailContentLocs: LocDefs<EmailContentLocs> = {
	SPEmailContentPlainTextFieldLabel: {
		defaultValue: 'Body',
		description: 'Label for plain text field.',
		displayPath: `${dpRoot}Plain Text Field Label`,
		displayType: 'Label',
		legacyName: 'Core/Catalogs.Emails.Details.FieldLabel.PlainText',
		...sharedProperties,
	},
	SPEmailContentHtmlBodyFieldLabel: {
		defaultValue: 'Body',
		description: 'Label for HTML body field.',
		displayPath: `${dpRoot}HTML Body Field Label`,
		displayType: 'Label',
		legacyName: 'Core/Catalogs.Emails.Details.FieldLabel.HTMLBody',
		...sharedProperties,
	},
	SPEmailContentNotificationTestSendComplete: {
		defaultValue: 'Test email sent',
		description: 'Notification message for test email sent.',
		displayPath: `${dpRoot}Test email sent`,
		displayType: 'Notification',
		legacyName: 'Core/Catalogs.Emails.Details.NotificationMessage.TestSendComplete',
		...sharedProperties,
	},
	SPEmailContentTitleSection: {
		defaultValue: 'Content',
		description: 'Section title.',
		displayPath: `${dpRoot}Section title`,
		displayType: 'Title',
		legacyName: 'Core/Catalogs.Emails.Details.Title.Content',
		...sharedProperties,
	},
	SPEmailContentButtonSendTest: {
		defaultValue: 'Send Test Email',
		description: 'Send Test Email button.',
		displayPath: `${dpRoot}Send Test Email`,
		displayType: 'Button',
		legacyName: 'Core/Catalogs.Emails.Details.Button.SendTest',
		...sharedProperties,
	},
	SPEmailContentButtonEdit: {
		defaultValue: 'Edit',
		description: 'Edit button.',
		displayPath: `${dpRoot}Edit`,
		displayType: 'Button',
		legacyName: 'Core/Catalogs.Emails.Details.Button.ContentEdit',
		...sharedProperties,
	},
	SPEmailContentButtonCancel: {
		defaultValue: 'Cancel',
		description: 'Cancel button.',
		displayPath: `${dpRoot}Cancel`,
		displayType: 'Button',
		legacyName: 'Core/Catalogs.Emails.Details.Button.ContentCancel',
		...sharedProperties,
	},
	SPEmailContentButtonSave: {
		defaultValue: 'Save',
		description: 'Save button.',
		displayPath: `${dpRoot}Save`,
		displayType: 'Button',
		legacyName: 'Core/Catalogs.Emails.Details.Button.ContentSave',
		...sharedProperties,
	},
	SPEmailContentLabelLanguage: {
		defaultValue: 'Language',
		description: 'Label for: Language.',
		displayPath: `${dpRoot}Language`,
		displayType: 'Label',
		legacyName: 'Core/Catalogs.Emails.Details.FieldLabel.Language',
		...sharedProperties,
	},
	SPEmailContentButtonAddLanguage: {
		defaultValue: 'Add Language',
		description: 'Button for: Add Language.',
		displayPath: `${dpRoot}Add Language`,
		displayType: 'Button',
		legacyName: 'Core/Catalogs.Emails.Details.Button.AddLanguage',
		...sharedProperties,
	},
	SPEmailContentLabelLastUpdatedBy: {
		defaultValue: 'Last Updated By',
		description: 'Label for: Last Updated By.',
		displayPath: `${dpRoot}Last Updated By`,
		displayType: 'Label',
		legacyName: 'Core/Catalogs.Emails.Details.FieldLabel.LastUpdatedBy',
		...sharedProperties,
	},
	SPEmailContentLabelLastUpdated: {
		defaultValue: 'Last Updated',
		description: 'Label for: Last Updated.',
		displayPath: `${dpRoot}Last Updated`,
		displayType: 'Label',
		legacyName: 'Core/Catalogs.Emails.Details.FieldLabel.LastUpdated',
		...sharedProperties,
	},
	SPEmailContentLabelSubject: {
		defaultValue: 'Subject',
		description: 'Label for: Subject.',
		displayPath: `${dpRoot}Subject`,
		displayType: 'Label',
		legacyName: 'Core/Catalogs.Emails.Details.FieldLabel.Subject',
		...sharedProperties,
	},
	SPEmailContentTitleSubstitutionTokens: {
		defaultValue: 'Substitution Tokens',
		description: 'Title for: Substitution Tokens.',
		displayPath: `${dpRoot}Substitution Tokens`,
		displayType: 'Title',
		legacyName: 'Core/Catalogs.Emails.Details.Title.Token',
		...sharedProperties,
	},
	SPEmailContentButtonHideTokens: {
		defaultValue: 'Hide Tokens',
		description: 'Button for: Hide Tokens.',
		displayPath: `${dpRoot}Hide Tokens`,
		displayType: 'Button',
		legacyName: 'Core/Catalogs.Emails.Details.Button.HideTokens',
		...sharedProperties,
	},
	SPEmailContentButtonShowTokens: {
		defaultValue: 'Show Tokens',
		description: 'Button for: Show Tokens.',
		displayPath: `${dpRoot}Show Tokens`,
		displayType: 'Button',
		legacyName: 'Core/Catalogs.Emails.Details.Button.ShowTokens',
		...sharedProperties,
	},
};

export type SPEmailContentLocType = LocMap<EmailContentLocs>;
